import { TrackingType, AreaProduct, TrackingIdentifier } from '../types-internal/AdobeTracking.js';

/**
 * This service used to handle all Adobe tracking operations in payment-frontend.
 */
class AdobeTrackingService {
    static CLICK_EVENT_GENERIC = 'Click Event Generic';
    static CLICK_TYPE = 'Click';
    static AUTOSHIPMENT_AREA = 'autoshipment';
    static track(type, area, identifier, trackingValue) {
        if (typeof window !== 'undefined' && typeof window.SendAdobeData === 'function') {
            /* Function signature: window.SendAdobeData(trackingType, areaProduct, trackingIdentifier, trackingValue)
             *
             * It accepts these params:
             *   - trackingType: The type of this tracking, according to what Daniel has explained, "this value indicates
             *                   that it is a generic tracking using our event 6 and evar13 storage".
             *   - areaProduct: Which area of the shop this event come from, it can be "Payment", "Account", "Loyalty",...
             *   - trackingIdentifier: The identifier.
             *   - trackingValue: The tracking value. This can be anything, but mostly a string, which is the event name.
             *
             * Read more at https://wiki.web.zooplus.de/x/dsadC for better explanation.
             **/
            window.SendAdobeData(type, area, identifier, trackingValue);
        }
        else {
            console.warn('[window.SendAdobeData] is undefined or not a function');
        }
    }
    /**
     * Send an Adobe tracking event for the "customClickTracking" type.
     *
     * @param area The area for tracking, e.g: Payment, Checkout,...
     * @param trackingIdentifier The identifier for this tracking. It can be "ApplePay", "Adyen",
     * or other identifier for different purposes.
     * @param trackingValue The value for this tracking. This can be anything, but mostly
     * a string, which is the event name.
     *
     * @see https://wiki.web.zooplus.de/x/dsadC Custom click tracking function SendAdobeData
     */
    static customClickTrack(area, trackingIdentifier, trackingValue) {
        this.track(TrackingType.CUSTOM_CLICK_TRACK, area, trackingIdentifier, trackingValue);
    }
    /**
     * Dispatch an Autoshipment event to be caught by the DII team.
     *
     * @param event The event details we want to dispatch
     */
    static dispatchAutoshipmentEvent = (event) => {
        /* For Autoshipment tracking operations, we will not directly invoke "window.SendAdobeData"
         * or "window._satellite", but dispatch an event instead. This event will be caught by the
         * DII team.
         * The implementation of this function is copied from "autoshipment-public-front" and discussed
         * with Sergio Rivas from Autoshipment team. */
        const evt = new CustomEvent('AboTracking', {
            detail: event,
        });
        window.dispatchEvent(evt);
    };
    /**
     * Send an Adobe tracking event. This function will internally call "window.SendAdobeData(event)"
     * with "event" is the parameter passed in.
     *
     * @param event The event to track
     */
    static commonTracking(event) {
        this.track(event);
    }
    static adyenTracking(eventType) {
        this.customClickTrack(AreaProduct.PAYMENT, TrackingIdentifier.ADYEN, eventType);
    }
    static checkoutTracking(identifier, value) {
        this.customClickTrack(AreaProduct.CHECKOUT, identifier, value);
    }
    /**
     * Tracking operations for PayPal Autoshipment button.
     */
    static payPalButtonSignClickedTrack() {
        this.dispatchAutoshipmentEvent({
            event: this.CLICK_EVENT_GENERIC,
            interaction: {
                type: this.CLICK_TYPE,
                name: 'paypal:agreement',
                details: {
                    area: this.AUTOSHIPMENT_AREA,
                    identifier: 'change payment',
                    targetLink: 'paypal agr btn',
                },
            },
        });
    }
    static payPalAgreementSuccessTrack() {
        this.dispatchAutoshipmentEvent({
            event: this.CLICK_EVENT_GENERIC,
            interaction: {
                type: this.CLICK_TYPE,
                name: 'paypal:agreement notification',
                details: {
                    area: this.AUTOSHIPMENT_AREA,
                    identifier: 'change payment',
                    targetLink: 'paypal agr OK',
                },
            },
        });
    }
    static payPalAgreementFailedTrack() {
        this.dispatchAutoshipmentEvent({
            event: this.CLICK_EVENT_GENERIC,
            interaction: {
                type: this.CLICK_TYPE,
                name: 'paypal:agreement notification',
                details: {
                    area: this.AUTOSHIPMENT_AREA,
                    identifier: 'change payment',
                    targetLink: 'paypal agr KO',
                },
            },
        });
    }
    /**
     * Send an Adobe Account Event tracking. This function calls "window.adbAccEvent.track"
     * internally.
     *
     * @param eventType The Account event type, e.g 'change_payment_method' used when
     * changing payment method
     * @param eventData The data for this event tracking
     */
    static accountEventTracking(eventType, eventData) {
        if (window?.adbAccEvent?.track) {
            window.adbAccEvent.track(eventType, eventData);
        }
        else {
            console.warn('[window.adbAccEvent.track] is undefined');
        }
    }
}

export { AdobeTrackingService as default };
//# sourceMappingURL=AdobeTrackingService.js.map
